/** Max days (bounds included) the API accepts in one custom range. */
export const RELOAD_RANGE_MAX_DAYS = 31;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Reads the calendar day of an API date (`YYYY-MM-DD…`) as local midnight, ignoring any time or offset. */
export function parseCalendarDay(value: string | null | undefined): Date | null {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

/** Formats the local calendar day as `YYYY-MM-DD`; never goes through UTC (`toISOString` shifts the day in Paris). */
export function formatCalendarDay(date: Date): string {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/** Inclusive number of calendar days between two local-midnight dates (DST-safe). */
export function inclusiveDays(start: Date, end: Date): number {
    return Math.round((end.getTime() - start.getTime()) / DAY_MS) + 1;
}

/** Earliest = campaign begin; latest = the smaller of campaign end and today. */
export function reloadRangeBounds(
    beginDate: string | null | undefined,
    endDate: string | null | undefined,
    today: Date,
): { min: Date | null; max: Date } {
    const todayStart = startOfDay(today);
    const campaignEnd = parseCalendarDay(endDate);
    return {
        min: parseCalendarDay(beginDate),
        max: campaignEnd && campaignEnd < todayStart ? campaignEnd : todayStart,
    };
}

/** Whether a picked range is complete, ordered, inside the bounds and at most 31 days long. */
export function isValidReloadRange(range: (Date | null)[] | null, bounds: { min: Date | null; max: Date }): range is [Date, Date] {
    if (!range || !range[0] || !range[1]) return false;
    const [start, end] = range;
    if (start > end) return false;
    if (bounds.min && start < bounds.min) return false;
    if (end > bounds.max) return false;
    return inclusiveDays(start, end) <= RELOAD_RANGE_MAX_DAYS;
}
